import { keepPreviousData, useMutation, useQuery, type QueryClient } from '@tanstack/react-query';
import { creditsApi } from '@/services/api/credits';
import { useOrgContextStore } from '@/stores/orgContextStore';
import type { CreditTransactionsParams } from '@/types/credits';

export const creditsKeys = {
  all: ['credits'] as const,
  balance: (activeOrgId: string | null) => [...creditsKeys.all, 'balance', activeOrgId] as const,
  ratios: () => [...creditsKeys.all, 'ratios'] as const,
  transactions: () => [...creditsKeys.all, 'transactions'] as const,
  transactionList: (activeOrgId: string | null, params: CreditTransactionsParams) =>
    [...creditsKeys.transactions(), activeOrgId, params] as const,
  packages: (activeOrgId: string | null) => [...creditsKeys.all, 'packages', activeOrgId] as const,
};

export function useCreditBalance() {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery({
    queryKey: creditsKeys.balance(activeOrgId),
    queryFn: () => creditsApi.getBalance(),
    staleTime: 1000 * 30,
  });
}

export function invalidateCreditBalance(queryClient: QueryClient, activeOrgId: string | null) {
  return queryClient.invalidateQueries({ queryKey: creditsKeys.balance(activeOrgId) });
}

export function useCreditRatios(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: creditsKeys.ratios(),
    queryFn: () => creditsApi.getRatios(),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled ?? true,
  });
}

export function useCreditTransactions(params: CreditTransactionsParams = {}) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const normalizedParams: CreditTransactionsParams = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 20,
    type: params.type,
    creditType: params.creditType,
    projectId: params.projectId,
    trackType: params.trackType,
    featureType: params.featureType,
    range: params.range,
    startDate: params.startDate,
    endDate: params.endDate,
    tzOffset: params.tzOffset,
  };

  return useQuery({
    queryKey: creditsKeys.transactionList(activeOrgId, normalizedParams),
    queryFn: () => creditsApi.getTransactions(normalizedParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30,
    // 结算中可能是等待确认或待对账，会长期保留暂占；只有运行中的任务需要追进度。
    refetchInterval: (query) =>
      normalizedParams.creditType !== 'credits' &&
      query.state.data?.items.some((item) => item.billingState === 'in_progress')
        ? 5000
        : false,
  });
}

export function useCreditPackages(options?: { enabled?: boolean }) {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);

  return useQuery({
    queryKey: creditsKeys.packages(activeOrgId),
    queryFn: () => creditsApi.getPackages(),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/**
 * 估算操作所需词元。
 */
export function useEstimateCredits() {
  return useMutation({
    mutationFn: (params: Parameters<typeof creditsApi.estimateCredits>[0]) =>
      creditsApi.estimateCredits(params),
  });
}
